import deviceApi from 'src/apis/device';
import { computed, reactive, onUnmounted, type ComputedRef } from 'vue';
import { useAuth } from 'src/composables/useAuth';
import { requestInterval, clearRequestInterval, type IntervalID } from 'src/lib/requestAnimationFrame';
import { POLLING_INTERVAL_MS } from 'src/consts/top';
import { getPreferences, setPreference } from 'src/services/preferences';
import type { CarSearchGroupScope } from 'src/consts';
import type { CarIndexParams } from 'src/models/apis/cars/carRequest';
import GICarManager from 'src/lib/geo_item/GICarManager';
import type { GeoItemsMeta, GICar, MapSelectOpts, TryRefreshCurrentSelectedElemInfoParams } from 'src/models/geoItem';
import type { Device } from 'src/models/device';

interface GroupScope {
  label: string;
  paramName: 'group2_id' | 'group3_id';
  paramValue: number;
}

export interface CarState {
  cars: GICar[];
  carsUpdatedAt: Date | null;
  devices: Device[];
  showCarIcons: boolean;
  carSearchParams: {
    groupScopeId: CarSearchGroupScope;
  };
}

export type CarContext = {
  carState: CarState;
  groupScopeMap: ComputedRef<Record<number, GroupScope>>;
  updateShowCarIcons: () => void;
  onCarListRowClick: (obj: GICar) => void;
  onCarSearchParamsChange: () => Promise<void>;
  fetchDevices: () => Promise<void>;
  initCar: () => Promise<void>;
};

export function useCar(
  geoItemsMeta: GeoItemsMeta,
  callbacks: {
    afterDataLayerItemClick: (dataName: string, obj: GICar, opts?: MapSelectOpts) => void;
    tryRefreshCurrentSelectedElemInfo: (p: TryRefreshCurrentSelectedElemInfoParams) => void;
  },
): CarContext {
  const auth = useAuth();
  const groupInfo = computed(() => auth.user?.groupInfo);

  const carState = reactive<CarState>({
    cars: [],
    carsUpdatedAt: null,
    devices: [],
    showCarIcons: true,
    carSearchParams: {
      groupScopeId: null,
    },
  });

  let carUpdateTimer: IntervalID | null = null;

  onUnmounted(() => {
    clearRequestInterval(carUpdateTimer);
  });

  const groupScopeMap = computed<Record<number, GroupScope>>(() => {
    const scopeMap: Record<number, GroupScope> = {};
    if (!groupInfo.value) return scopeMap;
    scopeMap[1] = { label: '事業所内', paramName: 'group2_id', paramValue: groupInfo.value.g2id };
    scopeMap[2] = { label: '部署内', paramName: 'group3_id', paramValue: groupInfo.value.g3id };
    return scopeMap;
  });

  const refreshCarLayer = () => {
    const giCarMeta = geoItemsMeta.map.car;
    giCarMeta.layerManager.refreshLayer(carState.cars);
  };

  const updateShowCarIcons = () => {
    carState.cars.forEach(car => {
      car.hideCarIcon = !carState.showCarIcons;
    });
    refreshCarLayer();
  };

  const onCarListRowClick = (obj: GICar) => {
    const isSelected = !obj.isSelected;
    carState.cars.forEach(e => { e.isSelected = false; });
    obj.isSelected = isSelected;
    refreshCarLayer();
    callbacks.afterDataLayerItemClick('car', obj, { moveCenter: true });
  };

  const getCarSearchParams = (): CarIndexParams => {
    const carParams: CarIndexParams = {};
    if (carState.carSearchParams.groupScopeId) {
      const groupScope = groupScopeMap.value[carState.carSearchParams.groupScopeId];
      if (groupScope) {
        carParams[groupScope.paramName] = groupScope.paramValue;
      }
    }
    return carParams;
  };

  const restartCarUpdateInterval = async() => {
    clearRequestInterval(carUpdateTimer);
    const pollCars = async() => {
      const giCarMeta = geoItemsMeta.map.car;
      const giCarManager: GICarManager = giCarMeta.manager as GICarManager;
      const newCars = await giCarManager.getResourcesByParams(getCarSearchParams());
      carState.cars = giCarManager.mergeStateInfo(newCars, carState.cars);
      giCarMeta.layerManager.refreshLayer(carState.cars);
      carState.carsUpdatedAt = new Date();
    };
    await pollCars();
    carUpdateTimer = requestInterval(pollCars, POLLING_INTERVAL_MS);
  };

  const onCarSearchParamsChange = async() => {
    await restartCarUpdateInterval();
    callbacks.tryRefreshCurrentSelectedElemInfo({
      dataName: 'car',
      resources: carState.cars,
      idFieldName: 'deviceId',
    });
    setPreference('car_search_group_scope', carState.carSearchParams.groupScopeId);
  };

  const fetchDevices = async() => {
    carState.devices = await deviceApi.index();
  };

  // onMountedの初期化シーケンスで呼ぶ (initializeDataManagers 完了後、車アイコン定期更新の開始)
  const initCar = async() => {
    const preferences = getPreferences();
    carState.carSearchParams.groupScopeId = preferences.car_search_group_scope || null;
    await restartCarUpdateInterval();
  };

  return {
    carState,
    groupScopeMap,
    updateShowCarIcons,
    onCarListRowClick,
    onCarSearchParamsChange,
    fetchDevices,
    initCar,
  };
}
